import React, { useMemo } from 'react';

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

const QuoteItemsTable = ({
  items = [],
  itemsSubtotal,
  orderDiscountAmount,
  totalAmount,
  netTotal,
  taxBreakdown = [],
  excludedTaxLabels = [],
}) => {
  const totals = useMemo(() => {
    const subtotal =
      typeof itemsSubtotal === 'number'
        ? itemsSubtotal
        : items.reduce((sum, item) => {
            if (!item || item.quantity <= 0) return sum;
            const unit = (Number(item.price) || 0) - (Number(item.discount) || 0);
            const baseTotal = Math.floor(unit * item.quantity);
            const taxAmount = Number.isFinite(Number(item.taxAmount))
              ? Number(item.taxAmount)
              : null;
            const netAmount = Number.isFinite(Number(item.netAmount))
              ? Number(item.netAmount)
              : taxAmount !== null
                ? baseTotal - taxAmount
                : baseTotal;
            const lineTotal =
              Number.isFinite(Number(item.netAmount)) && taxAmount !== null
                ? netAmount + taxAmount
                : baseTotal;
            return sum + lineTotal;
          }, 0);
    const discount = Number(orderDiscountAmount) || 0;
    const total = Number(totalAmount) || Math.max(subtotal - discount, 0);
    const breakdown = Array.isArray(taxBreakdown) ? taxBreakdown : [];
    const taxTotal = breakdown.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    const net = Number.isFinite(Number(netTotal)) ? Number(netTotal) : Math.max(total - taxTotal, 0);
    return { subtotal, discount, total, net, taxTotal, breakdown };
  }, [items, itemsSubtotal, orderDiscountAmount, totalAmount, netTotal, taxBreakdown]);

  const excludedNote =
    Array.isArray(excludedTaxLabels) && excludedTaxLabels.length > 0
      ? `Impuestos excluidos: ${excludedTaxLabels.join(', ')}`
      : null;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor neto</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor total</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((item, idx) => {
            const qty = Number(item.quantity) || 0;
            const unit = (Number(item.price) || 0) - (Number(item.discount) || 0);
            const subtotal = Math.floor(unit * qty);
            const taxAmount = Number.isFinite(Number(item.taxAmount)) ? Number(item.taxAmount) : null;
            const netAmount =
              Number.isFinite(Number(item.netAmount))
                ? Number(item.netAmount)
                : taxAmount !== null
                  ? subtotal - taxAmount
                  : subtotal;
            const totalAmountLine =
              Number.isFinite(Number(item.netAmount)) && taxAmount !== null
                ? netAmount + taxAmount
                : subtotal;
            return (
              <tr key={idx}>
                <td className="px-4 py-2 text-sm font-semibold text-gray-900">
                  {item.varietyName || item.productName || 'Producto'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">{item.metricUnit || 'unidad'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{qty}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(unit)}</td>
                <td className="px-4 py-2 text-sm font-semibold text-gray-900">{formatCurrency(netAmount)}</td>
                <td className="px-4 py-2 text-sm font-semibold text-gray-900">{formatCurrency(totalAmountLine)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}></td>
            <td className="px-4 py-2 text-right font-semibold text-sm">Valor neto</td>
            <td className="px-4 py-2 text-sm font-semibold">{formatCurrency(totals.net)}</td>
          </tr>
          {totals.breakdown.map((row) => (
            <tr key={row.taxId || row.name}>
              <td colSpan={4}></td>
              <td className="px-4 py-2 text-right font-semibold text-sm">
                {row.name || 'Impuesto'} {row.rate ? `(${row.rate}%)` : ''}
              </td>
              <td className="px-4 py-2 text-sm font-semibold">{formatCurrency(row.amount)}</td>
            </tr>
          ))}
          {excludedNote && (
            <tr>
              <td colSpan={6} className="px-4 py-2 text-xs text-gray-600">
                {excludedNote}
              </td>
            </tr>
          )}
          {totals.discount > 0 && (
            <tr>
              <td colSpan={4}></td>
              <td className="px-4 py-2 text-right font-semibold text-sm text-red-600">Descuento</td>
              <td className="px-4 py-2 text-sm font-semibold text-red-600">-{formatCurrency(totals.discount)}</td>
            </tr>
          )}
          <tr>
            <td colSpan={4}></td>
            <td className="px-4 py-2 text-right font-semibold text-sm">Total</td>
            <td className="px-4 py-2 text-sm font-bold text-blue-700">{formatCurrency(totals.total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default QuoteItemsTable;
